<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="offcanvas-header py-2 border-bottom" style="background: linear-gradient(135deg, #E96C15 0%, #d46010 100%);">
      <div class="d-flex align-items-center gap-2 text-white">
        <i class="fa-solid fa-layer-group" />
        <span class="fw-semibold">
          {{ type === 'CREATE' ? $t('Create Modifier Group') : $t('Edit Modifier Group') }}
        </span>
      </div>
      <button
        type="button"
        class="btn-close btn-close-white shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>

      <!-- Section: Group Information -->
      <div class="px-3 pt-3 pb-2">
        <div class="d-flex align-items-center mb-3">
          <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
          <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
            <i class="fa-solid fa-circle-info me-1" />
            {{ $t('Group Information') }}
          </span>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label for="name" class="form-label fw-medium small">
              {{ $t('Group Name (TH)') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter group name') + '...'"
              >
              <div v-for="error of v$.form.name.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label for="name_en" class="form-label fw-medium small">
              {{ $t('Group Name (EN)') }}
            </label>
            <div class="input-group-sm">
              <input
                id="name_en"
                type="text"
                v-model.trim="v$.form.name_en.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter group name in English') + '...'"
              >
              <div class="form-text text-muted">{{ $t('Leave blank to show the Thai name.') }}</div>
              <div v-for="error of v$.form.name_en.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label fw-medium small d-block">
              {{ $t('Selection Type') }}
              <span class="text-danger">*</span>
            </label>
            <div class="d-flex gap-2">
              <label
                class="d-flex align-items-center gap-2 px-2 rounded border flex-fill mb-0"
                :style="!form.allow_multiple ? 'border-color: #E96C15 !important; background: #fff7f2;' : 'border-color: #dee2e6; background: #f8f9fa;'"
                style="cursor: pointer; transition: all 0.15s; min-height: 32px;"
                for="allowMultiple1"
              >
                <input
                  class="form-check-input shadow-none m-0 flex-shrink-0"
                  type="radio"
                  v-model="v$.form.allow_multiple.$model"
                  @change="() => { this.form.options.forEach((opt) => { opt.is_default = false; }); }"
                  name="allowMultiple"
                  id="allowMultiple1"
                  :value="false"
                  style="width: 14px; height: 14px;"
                >
                <span style="font-size: 12px;">{{ $t('Single Select (Radio)') }}</span>
              </label>
              <label
                class="d-flex align-items-center gap-2 px-2 rounded border flex-fill mb-0"
                :style="form.allow_multiple ? 'border-color: #E96C15 !important; background: #fff7f2;' : 'border-color: #dee2e6; background: #f8f9fa;'"
                style="cursor: pointer; transition: all 0.15s; min-height: 32px;"
                for="inlineRadio2"
              >
                <input
                  class="form-check-input shadow-none m-0 flex-shrink-0"
                  type="radio"
                  v-model="v$.form.allow_multiple.$model"
                  @change="() => { this.form.options.forEach((opt) => { opt.is_default = false; }); }"
                  name="allowMultiple"
                  id="inlineRadio2"
                  :value="true"
                  style="width: 14px; height: 14px;"
                >
                <span style="font-size: 12px;">{{ $t('Multiple Select (Checkbox)') }}</span>
              </label>
            </div>
          </div>
          <!-- พฤติกรรมสูตร: ซ่อนเมื่อแพ็กไม่มี feature_inventory (ค่าเดิมยังส่งไปครบ ไม่ถูกล้าง) -->
          <div class="col-md-6" v-if="form.allow_multiple && $hasFeature('feature_inventory')">
            <label class="form-label fw-medium small">
              {{ $t('Recipe Behavior') }}
              <span class="text-danger">*</span>
            </label>
            <select2
              v-model.trim="v$.form.recipe_behavior.$model"
              :options="select2RecipeBehaviors.options"
              :settings="select2RecipeBehaviors.setting"
            />
          </div>
          <div class="col-md-3" v-if="form.allow_multiple && form.is_required">
            <label for="minSelection" class="form-label fw-medium small">
              {{ $t('Min. Selection') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="minSelection"
                type="number"
                v-model.trim="v$.form.min_options.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter min. selection') + '...'"
              >
              <div v-for="error of v$.form.min_options.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-3" v-if="form.allow_multiple && form.is_required">
            <label for="maxSelection" class="form-label fw-medium small">
              {{ $t('Max. Selection') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="maxSelection"
                type="number"
                v-model.trim="v$.form.max_options.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter max. selection') + '...'"
              >
              <div v-for="error of v$.form.max_options.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <label
              class="d-flex align-items-start gap-2 p-3 rounded border w-100 mb-0"
              :style="form.is_required ? 'border-color: #E96C15 !important; background: #fff7f2;' : 'border-color: #dee2e6; background: #f8f9fa;'"
              style="cursor: pointer; transition: all 0.15s;"
              for="checkRequired"
            >
              <input
                class="form-check-input shadow-none mt-1 flex-shrink-0"
                v-model.trim="v$.form.is_required.$model"
                type="checkbox"
                id="checkRequired"
                value="option1"
              >
              <div>
                <div class="fw-medium small">{{ $t('Required') }}</div>
                <div class="text-secondary" style="font-size: 13px;">
                  {{ $t('Customer must select at least one option.') }}
                </div>
              </div>
            </label>
          </div>
          <div class="col-md-12" id="tags-search">
            <label for="tags" class="form-label fw-medium small">
              <i class="fa-solid fa-tags me-1 text-secondary" />
              {{ $t('Tags') }}
            </label>
            <select2
              v-model="form.tags"
              :options="select2FormTags.options"
              :settings="select2FormTags.setting"
            />
          </div>
        </div>
      </div>

      <!-- Section: Options -->
      <div class="px-3 pt-3 pb-3">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-list-check me-1" />
              {{ $t('Options') }}
            </span>
            <span
              v-if="form.options.length > 0"
              class="badge ms-2 text-white"
              style="background: #E96C15; font-size: 11px;"
            >
              {{ form.options.length }}
            </span>
          </div>
          <ladda
            text="Add"
            :btn-class="'btn btn-sm btn-outline-primary'"
            icon="<i class='fa-solid fa-plus'></i>"
            @click="addOption"
          />
        </div>
        <div class="overflow-x-auto rounded border">
          <table class="table table-sm table-hover align-middle mb-0">
            <thead>
              <tr style="background: linear-gradient(135deg, #fff7f2 0%, #ffeee0 100%); border-bottom: 2px solid #eeb084;">
                <th style="width: 22%; min-width: 150px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Option Name (TH)') }}
                </th>
                <th style="width: 22%; min-width: 150px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Option Name (EN)') }}
                </th>
                <th class="text-end" style="width: 14%; min-width: 110px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Price') }}
                </th>
                <th style="width: 8%; min-width: 70px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Default') }}
                </th>
                <th v-if="$hasFeature('feature_inventory')" style="width: 22%; min-width: 140px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Recipe') }}
                </th>
                <th class="text-end" style="width: 12%; min-width: 90px; color: #7a3a00; font-size: 12px; font-weight: 600;">
                  {{ $t('Action') }}
                </th>
              </tr>
            </thead>
            <tbody class="align-baseline">
              <tr v-for="(option, index) in form.options" :key="index">
                <option-form
                  :index="index"
                  :option="option"
                  @set-default="setDefaultOption"
                />
              </tr>
              <tr v-if="form.options.length == 0">
                <td :colspan="$hasFeature('feature_inventory') ? 6 : 5" class="text-center py-4 text-secondary">
                  <i class="fa-regular fa-folder-open fa-2x mb-2" /><br>
                  <span class="small">{{ $t('No Available Data') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      class="bg-light border-top modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-sm me-2'"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-sm me-2'"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
  <ModalRecipeForm
    v-if="modalRecipeForm.open"
    :recipes="modalRecipeForm.recipes"
    :option-index="modalRecipeForm.optionIndex"
  />
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Modifiers } from '@/services/endpoint/modifiers.service';
import Select2 from 'vue3-select2-component';
import OptionForm from './OptionForm.vue';
import moment from "moment";
import signature  from '@/services/signature';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import numeral from 'numeral';
import ModalRecipeForm from './ModalRecipeForm.vue';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      selectInventory: [],
      listItems: [],
      loading: true,
      hasPermission: false,
      loadingSave: false,
      select2RecipeBehaviors: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      form: {
        name: '',
        name_en: '',
        allow_multiple: false,
        recipe_behavior: '',
        is_required: false,
        min_options: '',
        max_options: '',
        options: [],
        tags: []
      },
      optionsExpiryAnchors: [],
      modalRecipeForm: {
        open: false,
        recipes: [],
        optionIndex: ''
      },
      select2FormTags: {
        options: [],
        setting: {
          multiple: true,
          tags: true,
          placeholder: vm.$t('Search or add tags') + '...',
          maximumSelectionLength: 10,
          maximumInputLength: 20,
          language: {
            maximumSelected: function(e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            }
          },
          ajax: {
            cache: true,
            delay: 250,
            beforeSend: function(request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.MODIFIER_TAG,
            data: function(params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function(data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.name,
                  text: item.name,
                  color: item.color
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          },
          templateResult: this.templateResultCustomTag,
          templateSelection: this.templateSelectionCustomTag
        }
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    Select2,
    OptionForm,
    AccessDenied,
    ModalRecipeForm
  },
  methods: {
    templateResultCustomTag(option) {
      if (!option.id) return $('<div class="loading-small p-0" style="z-index:9999; height:0px;"></div>');
      if (!option.color) option.color = '#26ADCA';
      let html = "<div class='list-item d-flex templatecustom'>";
          html+= "  <div class='item-icon'>";
          html+= "    <i class='fa-solid fa-tag me-1' style='color:" + option.color + "'></i>";
          html+= "  </div>";
          html+= "  <div class='item-content'>" + option.text + "</div>";
          html+= "</div>";
      return $(html);
    },
    templateSelectionCustomTag(option) {
      if (!option.color) option.color = '#26ADCA';
      let html = "<div class='list-item d-flex templateselect' style='background:" + option.color + ";'>";
          html+= "  <div class='item-icon' style='color:#fff;'>";
          html+= "    <i class='fa-solid fa-tag me-1'></i>";
          html+= "  </div>";
          html+= "  <div class='item-content'>";
          html+= "    <div class='item-name' style='color:#fff;'>" + option.text + "</div>";
          html+= "  </div>";
          html+= "</div>";
      return $(html);
    },
    setDefaultOption(selectedIndex) {
      this.form.options.forEach((opt, index) => {
        opt.is_default = (index === selectedIndex);
      });
    },
    async deleteItem(index) {
      this.form.options.splice(index, 1);
    },
    addOption() {
      this.form.options.push({
        name: '',
        name_en: '',
        price_adjustment: '',
        is_default: false,
        split_exempt: false,
        recipes: []
      });
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    async getCreateData() {
      await Modifiers.getCreate().then(res => {
        this.select2RecipeBehaviors.options = res.data.data.recipe_behaviors.map(v => {
          return {
            id: v.value,
            text: this.$t(v.text)
          }
        });
        this.form.recipe_behavior = res.data.data.recipe_behaviors[0].value;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await Modifiers.getUpdate(this.id).then(res => {
        let vm = this;
        this.form = {
          name: res.data.data.name,
          name_en: res.data.data.name_en,
          allow_multiple: res.data.data.allow_multiple,
          recipe_behavior: res.data.data.recipe_behavior,
          is_required: res.data.data.required,
          min_options: (res.data.data.min_options > 0)? res.data.data.min_options: '',
          max_options: (res.data.data.max_options > 0)? res.data.data.max_options: '',
          options: res.data.data.options.map(function(option) {
            return {
              id: option.id,
              name: option.name,
              name_en: option.name_en,
              price_adjustment: (option.price > 0)? vm.formatNumberTwoDigit(option.price): "",
              is_default: option.default,
              split_exempt: !!option.split_exempt,
              recipes: option.recipes.map(function(recipe) {
                return {
                  inventory_id: recipe.inventory_id,
                  name: recipe.inventory_name,
                  quantity_display: vm.formatNumberTwoDigit(recipe.quantity_display),
                  quantity_display_solo: (recipe.quantity_display_solo > 0)? vm.formatNumberTwoDigit(recipe.quantity_display_solo): '',
                  quantity_system: vm.formatNumberTwoDigit(recipe.quantity_system),
                  quantity_system_solo: (recipe.quantity_system_solo > 0)? vm.formatNumberTwoDigit(recipe.quantity_system_solo): '',
                  unit_id: recipe.unit_id,
                  unit_name: recipe.unit_name
                }
              })
            }
          })
        };
        this.select2RecipeBehaviors.options = res.data.filter.recipe_behaviors.map(v => {
          return {
            id: v.value,
            text: this.$t(v.text)
          }
        });
        if (res.data.data.tags) {
          this.select2FormTags.options = res.data.data.tags.map(t => ({
            id: t.name,
            text: t.name,
            color: t.color
          }));
          this.form.tags = this.select2FormTags.options.map(t => t.id);
        }
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      if (!params.allow_multiple) params.recipe_behavior = 'stack';
      if (!params.allow_multiple || !params.is_required) params.min_options = params.max_options = '';
      // split_exempt/ปริมาณเดี่ยว มีความหมายเฉพาะกลุ่ม split_portion — กลุ่มอื่นล้างทิ้งก่อนส่ง
      const isSplitGroup = !!params.allow_multiple && params.recipe_behavior === 'split_portion';
      params.options = params.options.map(function(option) {
        option.price_adjustment = (option.price_adjustment > 0? option.price_adjustment: 0);
        option.split_exempt = isSplitGroup ? !!option.split_exempt : false;
        option.name_en = option.name_en || null;
        // recipes ว่างได้ (option ไม่ใช้วัตถุดิบ) — BE ลบสูตรเดิมให้เมื่อส่งว่าง
        option.recipes = option.recipes.map(function(recipe) {
          return {
            inventory_id: recipe.inventory_id,
            quantity_system: recipe.quantity_system,
            quantity_system_solo: (isSplitGroup && recipe.quantity_system_solo > 0)? recipe.quantity_system_solo: null,
            quantity_display: recipe.quantity_display,
            quantity_display_solo: (isSplitGroup && recipe.quantity_display_solo > 0)? recipe.quantity_display_solo: null,
            unit_id: recipe.unit_id
          }
        });
        return option;
      });
      $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
      // ชื่ออังกฤษเป็น optional — ส่ง null ไปด้วยตอนล้างค่า ไม่งั้น key ถูกตัดทิ้ง
      // ตาม rule "ค่าว่าง = ไม่ส่ง" ด้านบน แล้ว BE จะมองว่าไม่ได้แก้ = ค่าเดิมค้าง
      params.name_en = this.form.name_en || null;
      return params;
    },
    setParamsBeforeSendUpdate() {
      let params = _.cloneDeep(this.form);
      if (!params.allow_multiple) params.recipe_behavior = 'stack';
      if (!params.allow_multiple || !params.is_required) params.min_options = params.max_options = '';
      // split_exempt/ปริมาณเดี่ยว มีความหมายเฉพาะกลุ่ม split_portion — กลุ่มอื่นล้างทิ้งก่อนส่ง
      const isSplitGroup = !!params.allow_multiple && params.recipe_behavior === 'split_portion';
      params.options = params.options.map(function(option) {
        option.price_adjustment = (option.price_adjustment > 0? option.price_adjustment: 0);
        option.split_exempt = isSplitGroup ? !!option.split_exempt : false;
        option.name_en = option.name_en || null;
        // recipes ว่างได้ (option ไม่ใช้วัตถุดิบ) — BE ลบสูตรเดิมให้เมื่อส่งว่าง
        option.recipes = option.recipes.map(function(recipe) {
          return {
            inventory_id: recipe.inventory_id,
            quantity_system: recipe.quantity_system,
            quantity_system_solo: (isSplitGroup && recipe.quantity_system_solo > 0)? recipe.quantity_system_solo: null,
            quantity_display: recipe.quantity_display,
            quantity_display_solo: (isSplitGroup && recipe.quantity_display_solo > 0)? recipe.quantity_display_solo: null,
            unit_id: recipe.unit_id
          }
        });
        return option;
      });
      $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
      // ชื่ออังกฤษเป็น optional — ส่ง null ไปด้วยตอนล้างค่า ไม่งั้น key ถูกตัดทิ้ง
      // ตาม rule "ค่าว่าง = ไม่ส่ง" ด้านบน แล้ว BE จะมองว่าไม่ได้แก้ = ค่าเดิมค้าง
      params.name_en = this.form.name_en || null;
      return params;
    },
    // option ที่ไม่มีสูตร = ไม่ตัดสต็อก/ต้นทุนวัตถุดิบเป็น 0 — ยืนยันรวมครั้งเดียวก่อนบันทึก
    // กันเคส "ลืมใส่สูตร" ออกจากเคส "ตั้งใจไม่มี" โดยไม่ต้องมีสวิตช์รายแถว
    // แพ็กไม่มี feature_inventory = ไม่ตัดสต็อกอยู่แล้ว ไม่ต้องถาม (และร้านใส่สูตรไม่ได้ด้วย)
    async confirmNoRecipeOptions() {
      if (!this.$hasFeature('feature_inventory')) return true;
      const noRecipeNames = this.form.options
        .filter((option) => !option.recipes || option.recipes.length === 0)
        .map((option) => option.name || '-');
      if (noRecipeNames.length === 0) return true;
      const result = await this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title: this.$t('Some options have no recipe'),
        text: noRecipeNames.join(', ') + ' — ' + this.$t('They will not deduct stock and their ingredient cost will be 0.'),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary me-2",
          cancelButton: "btn btn-danger",
        },
        focusConfirm: false,
      });
      return result.isConfirmed;
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      if (!(await this.confirmNoRecipeOptions())) {
        this.loadingSave = false;
        return;
      }
      await Modifiers.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Modifier!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      if (!(await this.confirmNoRecipeOptions())) {
        this.loadingSave = false;
        return;
      }
      this.loadingSave = false;
      await Modifiers.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Modifier!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
  }
}
</script>
